import { env } from "@app/resolveModule/env";
import type Application from "@app/types/Application";
import type BaseCatalog from "@core/FileStructue/Catalog/BaseCatalog";
import type GitSourceData from "@ext/git/core/model/GitSourceData.schema";
import type GitSourceType from "@ext/git/core/model/GitSourceType";
import type Logger from "@ext/loggers/Logger";
import { span, trace } from "@ext/loggers/opentelemetry";
import SourceDataCtx from "@ext/storage/logic/SourceDataProvider/logic/SourceDataCtx";
import SourceType from "@ext/storage/logic/SourceDataProvider/model/SourceType";
import type { Workspace } from "@ext/workspace/Workspace";
import type WorkspaceManager from "@ext/workspace/WorkspaceManager";
import { releasePull, shouldSkipAutoPull, tryAcquirePull } from "./PullDebounce";

const DEFAULT_AUTO_PULL_INTERVAL = 180;
const DEFAULT_AUTO_PULL_DELAY = 100;

// Timed auto-pull interval in ms, or null when it must stay off.
// A negative AUTO_PULL_INTERVAL (e.g. -1) disables the timer entirely — catalogs then update via webhook only.
export const resolveAutoPullIntervalMs = (raw: string | number | undefined): number | null => {
	const seconds = Number(raw);
	if (seconds < 0) return null;
	return (seconds || DEFAULT_AUTO_PULL_INTERVAL) * 1000;
};

const toGitSourceType = (sourceType: SourceType): GitSourceType | null => {
	switch (sourceType) {
		case SourceType.git:
		case SourceType.gitHub:
		case SourceType.gitLab:
		case SourceType.gitVerse:
		case SourceType.gitea:
			return sourceType;
		default:
			return null;
	}
};

// Builds git credentials from a token. Returns null when the token is unset or the source is non-git —
// callers treat null as "pull disabled / skip" without an extra guard.
const buildSourceData = (
	sourceName: string,
	sourceType: SourceType,
	token: string,
	gitServerUsername: string,
): GitSourceData | null => {
	const gitSourceType = toGitSourceType(sourceType);
	if (!token || !gitSourceType) return null;

	const sourceData: GitSourceData = {
		sourceType: gitSourceType,
		domain: sourceName,
		userName: "autopull",
		gitServerUsername,
		userEmail: "autopull",
		token,
	};

	return SourceDataCtx.init<GitSourceData>(sourceData, null, null);
};

export type PullSourceDataProvider = (sourceName: string, sourceType: SourceType) => GitSourceData | null;

// Timed auto-pull credentials — gated by AUTO_PULL_TOKEN.
export const getAutoPullSourceData: PullSourceDataProvider = (sourceName, sourceType) =>
	buildSourceData(sourceName, sourceType, env("AUTO_PULL_TOKEN"), env("AUTO_PULL_USERNAME") || "autopull");

// Webhook-triggered pull credentials — prefer the dedicated WEBHOOK_TOKEN, fall back to AUTO_PULL_TOKEN so
// deployments that only configured auto-pull keep working when webhook pulls are introduced.
export const getWebhookSourceData: PullSourceDataProvider = (sourceName, sourceType) =>
	buildSourceData(
		sourceName,
		sourceType,
		env("WEBHOOK_TOKEN") || env("AUTO_PULL_TOKEN"),
		env("AUTO_PULL_USERNAME") || "autopull",
	);

export const pullCatalog = async (
	catalog: BaseCatalog,
	logger?: Logger,
	getSourceData: PullSourceDataProvider = getAutoPullSourceData,
): Promise<boolean> => {
	try {
		if (!catalog?.repo?.storage) return false;

		const storage = catalog.repo.storage;
		const sourceData = getSourceData(await storage.getSourceName(), await storage.getType());
		if (!sourceData) {
			span()?.addEvent("pull", { catalog: catalog.name, status: "skip", reason: "no source data" });
			return false;
		}

		if (await catalog.repo.isShouldSync({ data: sourceData, shouldFetch: true })) {
			logger?.logInfo(`Auto-pull started for "${catalog.name}" catalog`);
			await catalog.repo.sync({
				data: sourceData,
				onPull: () => {
					span()?.addEvent("pull", { catalog: catalog.name, status: "success" });
				},
			});
			return true;
		}
		span()?.addEvent("pull", { catalog: catalog.name, status: "skip" });
		return false;
	} catch (error) {
		span()?.addEvent("pull", { catalog: catalog.name, status: "error", message: String(error) });
		logger?.logWarning(`Error occurred while pulling "${catalog.name}" catalog: ${error}`);
		return false;
	}
};

export class AutoPull {
	private _wm: WorkspaceManager;
	private _logger: Logger;
	private _pullInterval: number;
	private _pullDelay: number;

	@trace()
	async start(app: Promise<Application>): Promise<void> {
		const { logger, wm } = await app;
		this._logger = logger;

		if (!env("AUTO_PULL_TOKEN")) {
			span()?.addEvent("auto-pull", { status: "disabled", reason: "no token set" });
			logger.logWarning("AUTO_PULL_TOKEN is not set. Auto-pull is disabled");
			return;
		}

		const intervalMs = resolveAutoPullIntervalMs(env("AUTO_PULL_INTERVAL"));
		if (intervalMs === null) {
			span()?.addEvent("auto-pull", { status: "disabled", reason: "negative interval" });
			logger.logInfo(
				"AUTO_PULL_INTERVAL is negative. Timed auto-pull is disabled; catalogs update via webhook only",
			);
			return;
		}

		this._wm = wm;
		this._pullInterval = intervalMs;
		this._pullDelay = Number(env("AUTO_PULL_DELAY")) || DEFAULT_AUTO_PULL_DELAY;

		span()?.addEvent("auto-pull", {
			status: "enabled",
			interval: this._pullInterval,
			delay: this._pullDelay,
		});
		logger.logInfo(`Auto pull enabled with interval: ${this._pullInterval}ms`);

		setTimeout(() => void this._pullCatalogs(this._wm.current()), this._pullInterval);
	}

	@trace()
	private async _pullCatalog(catalog: BaseCatalog): Promise<void> {
		await pullCatalog(catalog, this._logger);
	}

	private async _pullCatalogs(workspace: Workspace): Promise<void> {
		const catalogEntries = Array.from(workspace.getAllCatalogs().values());
		for (const catalogEntry of catalogEntries) {
			if (shouldSkipAutoPull(catalogEntry.name, this._pullInterval)) {
				span()?.addEvent("pull", { catalog: catalogEntry.name, status: "debounced" });
				continue;
			}
			// Share the in-flight guard with the webhook handler so auto-pull and a webhook-triggered
			// pull never run sync on the same catalog concurrently.
			if (!tryAcquirePull(catalogEntry.name)) {
				span()?.addEvent("pull", { catalog: catalogEntry.name, status: "in-flight" });
				continue;
			}
			try {
				await this._pullCatalog(catalogEntry);
			} finally {
				releasePull(catalogEntry.name);
			}
			await new Promise((r) => setTimeout(r, this._pullDelay));
		}
		setTimeout(() => void this._pullCatalogs(this._wm.current()), this._pullInterval);
	}
}

const initAutoPull = async (app: Promise<Application>): Promise<void> => {
	await new AutoPull().start(app);
};

export default initAutoPull;
